import QRCode from 'qrcode';

export interface QRCodeOptions {
  url: string;
  size?: number;
  padding?: number;
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  faviconUrl?: string;
  dotsColor?: string;
  qrBackground?: string;
  glowColor?: string;
}

export interface QRCodeData {
  dataUrl: string;
  svgString: string;
  modules: boolean[][];
  size: number;
  moduleCount: number;
  moduleSize: number;
  padding: number;
}

// Generate QR code data that matches the macOS app's exact style
export async function generateStyledQRCode(options: QRCodeOptions): Promise<QRCodeData> {
  const {
    url,
    size = 500,
    padding = 24,
    errorCorrectionLevel = 'H', // High error correction like Mac app
    faviconUrl,
    dotsColor,
    qrBackground,
    glowColor
  } = options;

  const qrSize = size - (padding * 2); // QR size without padding

  // Generate QR modules data first
  const qrArray = QRCode.create(url, { 
    errorCorrectionLevel, 
    version: undefined // Let library choose optimal version
  });
  
  const moduleCount = qrArray.modules.size;
  const moduleSize = qrSize / moduleCount;
  
  // Convert QR modules to boolean array
  const modules: boolean[][] = [];
  for (let row = 0; row < moduleCount; row++) {
    modules[row] = [];
    for (let col = 0; col < moduleCount; col++) {
      modules[row][col] = qrArray.modules.get(row, col) === 1;
    }
  }

  // Generate styled canvas-based data URL with custom colors
  const dataUrl = await generateMacAppStyleQR(modules, size, padding, faviconUrl, dotsColor, qrBackground);
  
  // Generate SVG representation
  const svgString = generateStyledSVG(modules, size, padding, dotsColor, qrBackground);

  return {
    dataUrl,
    svgString,
    modules,
    size,
    moduleCount,
    moduleSize,
    padding
  };
}

// Generate QR code exactly matching Mac app style
async function generateMacAppStyleQR(
  modules: boolean[][],
  totalSize: number,
  padding: number,
  faviconUrl?: string,
  dotsColor?: string,
  qrBackground?: string
): Promise<string> {
  // Create a canvas element for browser compatibility
  const canvas = document.createElement('canvas');
  canvas.width = totalSize;
  canvas.height = totalSize;
  const ctx = canvas.getContext('2d')!;

  const qrSize = totalSize - (padding * 2);
  const moduleCount = modules.length;
  const moduleSize = qrSize / moduleCount;
  const dotRadius = moduleSize * 0.45; // Exact match to Mac app

  // Use custom colors if provided, otherwise use light theme defaults
  const backgroundColor = qrBackground || '#ffffff';
  const dotColor = dotsColor || '#000000';

  // Fill background including padding
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, totalSize, totalSize);

  // Draw data modules as circular dots
  ctx.fillStyle = dotColor;
  
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      // Skip finder pattern areas (8x8 including separator)
      const isInFinderPatternArea = 
        (row < 8 && col < 8) || // Top-left
        (row < 8 && col >= moduleCount - 8) || // Top-right
        (row >= moduleCount - 8 && col < 8); // Bottom-left

      if (isInFinderPatternArea) continue;

      // Skip center icon area (5-module radius)
      const centerRow = Math.floor(moduleCount / 2);
      const centerCol = Math.floor(moduleCount / 2);
      const iconExclusionRadius = 5.0;
      
      const rowDistance = row - centerRow;
      const colDistance = col - centerCol;
      const distanceFromCenter = Math.sqrt(rowDistance * rowDistance + colDistance * colDistance);
      
      if (distanceFromCenter <= iconExclusionRadius) continue;

      if (modules[row][col]) {
        const x = padding + (col * moduleSize) + (moduleSize / 2);
        const y = padding + ((moduleCount - 1 - row) * moduleSize) + (moduleSize / 2); // Flip Y coordinate
        
        ctx.beginPath();
        ctx.arc(x, y, dotRadius, 0, 2 * Math.PI);
        ctx.fill();
      }
    }
  }

  // Draw finder patterns with squircle design
  drawFinderPatterns(ctx, moduleSize, moduleCount, padding, qrSize, dotsColor, qrBackground);
  
  // Draw center icon (favicon or globe)
  await drawCenterIcon(ctx, qrSize, moduleSize, dotRadius, moduleCount, padding, faviconUrl);

  // Convert to data URL
  return canvas.toDataURL('image/png');
}

function drawFinderPatterns(
  ctx: CanvasRenderingContext2D,
  moduleSize: number,
  moduleCount: number,
  padding: number,
  qrSize: number,
  dotsColor?: string,
  qrBackground?: string
) {
  // Use custom colors if provided, otherwise use light theme defaults
  const outerColor = dotsColor || '#000000';
  const innerColor = qrBackground || '#ffffff';
  const centerColor = dotsColor || '#000000';

  const positions = [
    { // Top-left
      x: padding + moduleSize,
      y: padding + (qrSize - (moduleSize * 8))
    },
    { // Top-right  
      x: padding + (qrSize - (moduleSize * 8)),
      y: padding + (qrSize - (moduleSize * 8))
    },
    { // Bottom-left
      x: padding + moduleSize,
      y: padding + moduleSize
    }
  ];

  positions.forEach(pos => {
    const outerSize = moduleSize * 7;
    const outerCornerRadius = moduleSize * 2.0;
    
    // Draw outer squircle
    ctx.fillStyle = outerColor;
    roundRect(ctx, pos.x, pos.y, outerSize, outerSize, outerCornerRadius);
    
    // Draw middle squircle
    const middleSize = moduleSize * 5;
    const middleCornerRadius = moduleSize * 1.5;
    ctx.fillStyle = innerColor;
    roundRect(ctx, pos.x + moduleSize, pos.y + moduleSize, middleSize, middleSize, middleCornerRadius);
    
    // Draw inner squircle
    const innerSize = moduleSize * 3;
    const innerCornerRadius = moduleSize * 0.8;
    ctx.fillStyle = centerColor;
    roundRect(ctx, pos.x + (moduleSize * 2), pos.y + (moduleSize * 2), innerSize, innerSize, innerCornerRadius);
  });
}

async function drawCenterIcon(
  ctx: CanvasRenderingContext2D,
  qrSize: number,
  moduleSize: number,
  dotRadius: number,
  moduleCount: number,
  padding: number,
  faviconUrl?: string
) {
  const centerX = padding + (qrSize / 2);
  const centerY = padding + (qrSize / 2);
  const iconSize = dotRadius * 2 * 8; // 8x the dot diameter like Mac app
  const iconRadius = iconSize / 2;

  // Try to draw favicon first if available
  if (faviconUrl) {
    try {
      const faviconDrawn = await drawFavicon(ctx, centerX, centerY, iconSize, faviconUrl);
      if (faviconDrawn) {
        return; // Successfully drew favicon, exit early
      }
    } catch (error) {
      console.log('Failed to draw favicon, falling back to globe:', error);
    }
  }

  // Fallback to globe icon with background (Mac app style)
  drawIconBackground(ctx, centerX, centerY, iconRadius);
  drawGlobeIcon(ctx, centerX, centerY, iconRadius, dotRadius);
}

function drawIconBackground(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  iconRadius: number
) {
  // Light theme colors (exact match to Mac app)
  const backgroundColor = 'rgba(51, 153, 255, 0.7)'; // Geist Blue 500 (P3) with alpha
  const strokeColor = 'rgba(0, 0, 0, 0.2)'; // Geist Gray 900 (P3) with alpha

  // Create radial gradient background (like Mac app)
  const gradientCenter = 'rgba(51, 153, 255, 0.7)'; // Geist Blue 500 (P3)
  const gradientEdge = 'rgba(51, 153, 255, 0.1)'; // Fade to edge

  // Draw radial gradient background
  const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, iconRadius);
  gradient.addColorStop(0, gradientCenter);
  gradient.addColorStop(1, gradientEdge);
  
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(centerX, centerY, iconRadius, 0, 2 * Math.PI);
  ctx.fill();

  // Add a subtle border for better definition (like Mac app)
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = 1.0;
  ctx.beginPath();
  ctx.arc(centerX, centerY, iconRadius, 0, 2 * Math.PI);
  ctx.stroke();
}

async function drawFavicon(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  iconSize: number,
  faviconUrl: string
): Promise<boolean> {
  try {
    // Create a temporary canvas to load and process the favicon
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = iconSize;
    tempCanvas.height = iconSize;
    const tempCtx = tempCanvas.getContext('2d')!;
    
    // Create an image element to load the favicon
    const image = new Image();
    image.crossOrigin = 'anonymous'; // Handle CORS
    
    return new Promise((resolve) => {
      image.onload = () => {
        // Check for generic favicons (like Mac app does)
        if (image.width < 16 || image.height < 16) {
          resolve(false);
          return;
        }
        
        // Use a slightly smaller size for the favicon to provide quiet space around it
        const faviconSize = iconSize * 0.7; // 70% of the icon size for quiet space
        
        // Fill with transparent background first
        tempCtx.clearRect(0, 0, faviconSize, faviconSize);
        
        // Draw the icon directly without clipping (like Mac app)
        tempCtx.drawImage(image, 0, 0, faviconSize, faviconSize);
        
        // Draw the processed favicon onto the main canvas, centered
        const iconRect = {
          x: centerX - faviconSize / 2,
          y: centerY - faviconSize / 2,
          width: faviconSize,
          height: faviconSize
        };
        
        ctx.drawImage(tempCanvas, iconRect.x, iconRect.y, iconRect.width, iconRect.height);
        resolve(true);
      };
      
      image.onerror = () => {
        resolve(false);
      };
      
      image.src = faviconUrl;
    });
  } catch (error) {
    console.log('Error drawing favicon:', error);
    return false;
  }
}

function drawGlobeIcon(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  iconRadius: number,
  dotRadius: number
) {
  // Light theme colors (matching Mac app exactly)
  const strokeColor = 'rgba(0, 0, 0, 1.0)';

  // Draw globe lines (background is already drawn by drawIconBackground)
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = dotRadius * 2; // Same width as dots

  // Main circle border
  ctx.beginPath();
  ctx.arc(centerX, centerY, iconRadius, 0, 2 * Math.PI);
  ctx.stroke();

  // Horizontal line (equator)
  ctx.beginPath();
  ctx.moveTo(centerX - iconRadius, centerY);
  ctx.lineTo(centerX + iconRadius, centerY);
  ctx.stroke();

  // Vertical ellipse (longitude)
  ctx.beginPath();
  ctx.ellipse(centerX, centerY, iconRadius * 0.6, iconRadius, 0, 0, 2 * Math.PI);
  ctx.stroke();
}

// Helper function for rounded rectangles
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  ctx.fill();
}

// Generate SVG representation
function generateStyledSVG(
  modules: boolean[][],
  totalSize: number,
  padding: number,
  dotsColor?: string,
  qrBackground?: string
): string {
  const qrSize = totalSize - (padding * 2);
  const moduleCount = modules.length;
  const moduleSize = qrSize / moduleCount;
  const dotRadius = moduleSize * 0.45;

  const backgroundColor = qrBackground || '#ffffff';
  const dotColor = dotsColor || '#000000';

  let svgContent = `<svg width="${totalSize}" height="${totalSize}" viewBox="0 0 ${totalSize} ${totalSize}" xmlns="http://www.w3.org/2000/svg">`;
  
  // Background
  svgContent += `<rect width="${totalSize}" height="${totalSize}" fill="${backgroundColor}"/>`;
  
  // Data modules as circles
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      // Skip finder patterns and center area
      const isInFinderPatternArea = 
        (row < 8 && col < 8) ||
        (row < 8 && col >= moduleCount - 8) ||
        (row >= moduleCount - 8 && col < 8);

      if (isInFinderPatternArea) continue;

      const centerRow = Math.floor(moduleCount / 2);
      const centerCol = Math.floor(moduleCount / 2);
      const rowDistance = row - centerRow;
      const colDistance = col - centerCol;
      const distanceFromCenter = Math.sqrt(rowDistance * rowDistance + colDistance * colDistance);
      
      if (distanceFromCenter <= 5.0) continue;

      if (modules[row][col]) {
        const x = padding + (col * moduleSize) + (moduleSize / 2);
        const y = padding + (row * moduleSize) + (moduleSize / 2);
        svgContent += `<circle cx="${x}" cy="${y}" r="${dotRadius}" fill="${dotColor}"/>`;
      }
    }
  }

  // Add finder patterns and center icon using simple shapes for SVG
  // (For production, you'd want to implement full SVG equivalents)
  svgContent += `<text x="50%" y="50%" text-anchor="middle" dy="0.3em" fill="${dotColor}" font-family="Arial" font-size="12">QR</text>`;
  
  svgContent += '</svg>';
  return svgContent;
}
